import get from "lodash.get";

import { getShippingThresholdByOffer } from "@intelligems/ig-utils";
import { createProgressBarConfigForOffer } from "@intelligems/ig-utils/widgets/createProgressBarConfigForOffer";
import { createProgressBarConfigForSitewide } from "@intelligems/ig-utils/widgets/createProgressBarConfigForSitewide";
import { resolveProgressBarWidgetConfig } from "@intelligems/ig-widgets/ProgressBarView/resolveProgressBarWidgetConfig";
import { createSelector } from "@reduxjs/toolkit";
import { RootState, useSelector, useSelectorFactory } from "@src/store";
import { selectedActivatedModArray } from "@src/store/slices/experienceInfoState";
import { getVariationOffer } from "@src/store/slices/offers/selectors";
import {
  getStoredUserInterfaces,
  getUserInterfaces,
} from "@src/store/slices/userInterfaces";
import {
  getUserInterfaceProgressBar,
  isDiscountProgressBarEnabled,
  isMessageBoxPopupEnabled,
  isMessageBoxSlideOutEnabled,
  isQuantityButtonsEnabled,
  isShippingProgressBarEnabled,
} from "@src/store/slices/widgets/selectors";
import { getDiscountThresholdByOffer } from "@src/utils/progressBar/getDiscountThresholdByOffer";

import {
  CartValueConfig,
  UserInterface,
  UserInterfaceConfig,
  VariationUserInterfaces,
} from "./types";
import {
  isMessageBoxInterface,
  isProgressBarInterface,
  isQuantityButtonsInterface,
  resolveEnabledWidgets,
} from "./util";

const getUserInterfaceMap = createSelector(
  getUserInterfaces,
  (userInterfaces) =>
    userInterfaces.reduce(
      (map, userInterface) => {
        const { id } = userInterface;
        map[id] = userInterface;
        return map;
      },
      {} as Record<string, UserInterface>
    )
);

const getStoredUserInterfaceMap = createSelector(
  getStoredUserInterfaces,
  (userInterfaces) =>
    userInterfaces.reduce(
      (map, userInterface) => {
        const { id } = userInterface;
        map[id] = userInterface;
        return map;
      },
      {} as Record<string, UserInterface>
    )
);

export const getUserInterface = (interfaceId: string) => (state: RootState) =>
  getUserInterfaceMap(state)[interfaceId] as UserInterface | undefined;

export const getStoredUserInterface =
  (interfaceId: string) => (state: RootState) =>
    getStoredUserInterfaceMap(state)[interfaceId] as UserInterface | undefined;

const getVariationUserInterfaceMap = createSelector(
  getUserInterfaces,
  (userInterfaces) =>
    userInterfaces.reduce(
      (map, userInterface) => {
        const { variationId } = userInterface;
        if (variationId) {
          const current = map[variationId] ?? (map[variationId] = {});
          if (isMessageBoxInterface(userInterface)) {
            current.messageBox = userInterface;
          } else if (isProgressBarInterface(userInterface)) {
            current.progressBar = userInterface;
          } else if (isQuantityButtonsInterface(userInterface)) {
            current.quantityButtons = userInterface;
          }
        }
        return map;
      },
      {} as Record<string, VariationUserInterfaces>
    )
);

export const getQuantityButtonsInterface =
  (interfaceId: string) => (state: RootState) => {
    const userInterface = getUserInterface(interfaceId)(state);
    return isQuantityButtonsInterface(userInterface)
      ? userInterface
      : undefined;
  };

export const getQuantityButtonsInterfaceForVariation = (variationId: string) =>
  createSelector(
    getVariationUserInterfaceMap,
    isQuantityButtonsEnabled(variationId),
    (map, quantityButtonsEnabled) => {
      const userInterface = map[variationId]?.quantityButtons;
      if (!userInterface) return undefined;

      if (!quantityButtonsEnabled) {
        return { ...userInterface, widget: undefined };
      }
      return userInterface;
    }
  );
export const useQuantityButtonsInterfaceForVariation = (variationId: string) =>
  useSelectorFactory(getQuantityButtonsInterfaceForVariation, variationId);

export const getProgressBarInterface =
  (interfaceId: string) => (state: RootState) => {
    const userInterface = getUserInterface(interfaceId)(state);
    return isProgressBarInterface(userInterface) ? userInterface : undefined;
  };

export const getProgressBarInterfaceForVariation = (variationId: string) =>
  createSelector(
    getVariationUserInterfaceMap,
    isDiscountProgressBarEnabled(variationId),
    isShippingProgressBarEnabled(variationId),
    (map, discountProgressBarEnabled, shippingProgressBarEnabled) => {
      const userInterface = map[variationId]?.progressBar;
      if (!userInterface) return undefined;

      if (
        userInterface.widget?.widgetType === "discountProgressBar" &&
        !discountProgressBarEnabled
      ) {
        return { ...userInterface, widget: undefined };
      }
      if (
        userInterface.widget?.widgetType === "shippingProgressBar" &&
        !shippingProgressBarEnabled
      ) {
        return { ...userInterface, widget: undefined };
      }
      return userInterface;
    }
  );
export const useProgressBarInterfaceForVariation = (variationId: string) =>
  useSelectorFactory(getProgressBarInterfaceForVariation, variationId);

export const getUserInterfaceProgressBarConfig = (variationId: string) =>
  createSelector(
    getVariationOffer(variationId),
    getUserInterfaceProgressBar(variationId),
    (offer, widget) => {
      if (!widget) return undefined;

      const widgetConfig = resolveProgressBarWidgetConfig(widget, offer);
      if (offer) {
        return createProgressBarConfigForOffer(offer, widgetConfig, true);
      } else {
        return createProgressBarConfigForSitewide(widgetConfig);
      }
    }
  );
export const useUserInterfaceProgressBarConfig = (variationId: string) =>
  useSelectorFactory(getUserInterfaceProgressBarConfig, variationId);

export const getMessageBoxInterface =
  (interfaceId: string) => (state: RootState) => {
    const userInterface = getUserInterface(interfaceId)(state);
    return isMessageBoxInterface(userInterface) ? userInterface : undefined;
  };

export const getMessageBoxInterfaceForVariation = (variationId: string) =>
  createSelector(
    getVariationUserInterfaceMap,
    isMessageBoxPopupEnabled(variationId),
    isMessageBoxSlideOutEnabled(variationId),
    (map, messageBoxPopupEnabled, messageBoxSlideOutEnabled) => {
      const userInterface = map[variationId]?.messageBox;
      if (!userInterface) return undefined;

      if (
        userInterface.widget?.widgetType === "messageBoxPopup" &&
        !messageBoxPopupEnabled
      ) {
        return { ...userInterface, widget: undefined };
      }
      if (
        userInterface.widget?.widgetType === "messageBoxSlideOut" &&
        !messageBoxSlideOutEnabled
      ) {
        return { ...userInterface, widget: undefined };
      }
      return userInterface;
    }
  );

export const hasQuantityButtonsUserInterface =
  (variationId: string) => (state: RootState) => {
    const userInterface =
      getQuantityButtonsInterfaceForVariation(variationId)(state);
    return userInterface && userInterface.isEnabled && userInterface.widget;
  };

export const hasProgressBarUserInterface =
  (variationId: string) => (state: RootState) => {
    const userInterface =
      getProgressBarInterfaceForVariation(variationId)(state);
    return userInterface && userInterface.isEnabled && userInterface.widget;
  };

export const hasMessageBoxUserInterface =
  (variationId: string) => (state: RootState) => {
    const userInterface =
      getMessageBoxInterfaceForVariation(variationId)(state);
    return userInterface && userInterface.isEnabled && userInterface.widget;
  };

export const getUserInterfaceVariable =
  (interfaceId: string, key: string) => (state: RootState) => {
    return get(
      getUserInterface(interfaceId)(state)?.desktopVariables,
      key
    ) as unknown;
  };

export const getUserInterfaceOfferTierCount =
  (variationId: string) => (state: RootState) => {
    const offer = getVariationOffer(variationId)(state);
    return offer?.tiers?.length ?? 0;
  };

export const getUserInterfaceOfferTierId =
  (variationId: string, tierIndex: number) => (state: RootState) => {
    const offer = getVariationOffer(variationId)(state);
    return offer?.tiers[tierIndex]?.id;
  };

export const getUserInterfaceOfferTier =
  (variationId: string, tierIndex: number) => (state: RootState) => {
    const offer = getVariationOffer(variationId)(state);
    return offer?.tiers[tierIndex];
  };

export const getUserInterfaceConfigForOffer = (variationId: string) =>
  createSelector(
    getUserInterfaceProgressBar(variationId),
    getVariationOffer(variationId),
    selectedActivatedModArray,
    isQuantityButtonsEnabled(variationId),
    isDiscountProgressBarEnabled(variationId),
    isShippingProgressBarEnabled(variationId),
    isMessageBoxPopupEnabled(variationId),
    isMessageBoxSlideOutEnabled(variationId),
    (
      progressBar,
      offer,
      modArray,
      quantityButtonsEnabled,
      discountProgressBarsEnabled,
      shippingProgressBarsEnabled,
      messageBoxPopupsEnabled,
      messageBoxSlideOutsEnabled
    ): UserInterfaceConfig => {
      const cartValue: CartValueConfig = {
        mode: "input",
        threshold: 0,
        unitType: offer?.unitType ?? "unit",
      };
      if (progressBar?.widgetType === "shippingProgressBar") {
        cartValue.mode = "toggle";
        cartValue.threshold = getShippingThresholdByOffer(offer) ?? 1;
      } else if (modArray.includes("tieredDiscount")) {
        cartValue.mode = "input";
        cartValue.threshold = getDiscountThresholdByOffer(offer) ?? 1;
      } else {
        cartValue.mode = "toggle";
        cartValue.threshold = getDiscountThresholdByOffer(offer) ?? 1;
      }
      return {
        enabled: resolveEnabledWidgets({
          quantityButtonsEnabled,
          discountProgressBarsEnabled,
          shippingProgressBarsEnabled,
          messageBoxPopupsEnabled,
          messageBoxSlideOutsEnabled,
        }),
        cartValue,
      };
    }
  );

export const useUserInterfaceConfigForOffer = (variationId: string) => {
  return useSelectorFactory(getUserInterfaceConfigForOffer, variationId);
};

export const getUserInterfaceConfigForShippingTest = (variationId: string) =>
  createSelector(getVariationOffer(variationId), (_offer) => {
    return {
      enabled: resolveEnabledWidgets({
        shippingProgressBarsEnabled: true,
      }),
      cartValue: {
        mode: "toggle",
        threshold: 100,
        unitType: "dollar",
      } as const,
    };
  });

export const useUserInterfaceConfigForShippingTest = (variationId: string) => {
  return useSelector(getUserInterfaceConfigForShippingTest(variationId));
};
